import { NhstAuthClient, ZephrPublicClient, SubAuthClient } from '../public-clients';
import Utils from '../helpers/utils';
import { BaseServices } from './BaseServices';
import { getServerLog } from '../helpers/ServerLogStorageUtil';

function normalizeFailureReason(cause) {
  if (cause == null || cause === '') return 'unknown';
  return String(cause).toLowerCase().replace(/[\s-]+/g, '_');
}

// New NhstTracking script-embed emitter. Matches the auth_failure v1 producer
// contract: SYSTEM_ACTION event with the canonical 3-slot failure decomposition
// (action=fail, identifier_type=auth, identifier_value=login). Returns false
// when window.NhstTracking is not initialized.
function triggerLoginFailEventNhst(cause) {
  if (!window.NhstTracking) {
    return false;
  }
  window.NhstTracking.track({
    event: 'system_action',
    sub_application_name: 'nhst-sub-auth-services',
    sub_application_version: '7.1.4',
    sub_producer_contract: {
      name: 'auth_failure',
      version: '1'
    },
    attributes: {
      action: 'fail',
      identifier_type: 'auth',
      identifier_value: 'login',
      // Azure-sourced reasons (e.g. INVALID_CREDENTIALS, MFA-Required) are
      // normalized to lower_snake_case before emission. Empty → 'unknown'.
      failure_reason: normalizeFailureReason(cause)
    }
  },
  // As it is a critical data for D&I, we want to send it immediately
  { immediate: true });
  return true;
}

class AuthServices extends BaseServices {
  constructor(config) {
    super(config);
    this.nhstAuthClientConfig = this.config.nhstAuthClientConfig || {};
    this.zephrPublicClientConfig = this.config.zephrPublicClientConfig || {};
    this.subAuthClientConfig = this.config.subAuthClientConfig || {};
    this.nhstAuthClient = new NhstAuthClient(this.nhstAuthClientConfig);
    this.zephrPublicClient = new ZephrPublicClient(this.zephrPublicClientConfig);
    this.subAuthClient = new SubAuthClient(this.subAuthClientConfig);
  }

  ssoPopupShowedKey(publicationName) {
    return `${publicationName}-sso-popup-showed`;
  }

  async updateSsoMeta(publicationName, ssoData = {}) {
    const data = ssoData;
    data[this.ssoPopupShowedKey(publicationName)] = true;
    this.logger.info('Meta to update', data);
    try {
      await this.zephrPublicClient.updateSsoStatusMeta(data);
    } catch (e) {
      this.logger.error(e);
    }
    return {};
  }

  triggerLoginFailEvent(code, cause) {
    if (Utils.isServerSide()) {
      return false;
    }
    // 300 and 303 error codes are ignored as  we use it internally, see NhstLoginStart.vue
    if ([300, 303].includes(code) || (code >= 200 && code < 299)) {
      return false;
    }
    return triggerLoginFailEventNhst(cause);
  }

  deleteCache() {
    const deleteIndexDB = (indexedDBName) => {
      // Check if IndexedDB is supported in the current browser
      if (!('indexedDB' in window)) {
        console.error('IndexedDB is not supported in this browser.');
        return;
      }
      const request = window.indexedDB.deleteDatabase(indexedDBName);
      request.onsuccess = () => {
        console.log(`Database "${indexedDBName}" deleted successfully.`);
      };
      request.onerror = () => {
        console.error(`Error deleting database "${indexedDBName}": ${request.error.message}`);
      };

      request.onblocked = () => {
        console.error(`Can't delete the database "${indexedDBName}" because it's still open in another tab.`);
      };
    };
    ['article-dn', 'stream-dn'].forEach((db) => deleteIndexDB(db));
  }

  async sendEvent(eventName, eventDetails, username, extraData) {
    try {
      await this.nhstAuthClient.sendEvent(eventName, eventDetails, username, extraData);
    } catch (e) {
      this.logger.error(e);
    }
  }

  async postCookieUpdateTasks(data) {
    Utils.storeInLocalStorage(
      'sub-user-data',
      data,
      1,
      'day'
    );
    if (Utils.isIOSApp()) {
      try {
        window.webkit.messageHandlers.cookiesSynced.postMessage({
          type: 'cookie_syncronization',
          session_valid: true,
          cookies_updated: true,
        });
      } catch (e) {
      }
    }
    if (Utils.isAndroidApp()) {
      try {
        await this.subAuthClient.cookiesSynchronizationPing(true, true);
      } catch (e) {
      }
    }
  }

  async refreshUser() {
    this.logger.info('Refreshing user');
    try {
      const res = await this.nhstAuthClient.refreshUser();
      this.logger.info(`[Refresh user success]`);
      await this.postCookieUpdateTasks(res.data);
      return res;
    } catch (e) {
      this.logger.error(`[Refresh user error] ${e}`);
    } finally {
      this.deleteCache();
    }
  }

  async createOrUpdateCookies() {
    this.logger.info('Create or update Cookies');
    try {
      const res = await this.nhstAuthClient.cookies();
      this.logger.info(`[Cookie create/update success]`);
      await this.postCookieUpdateTasks(res.data);
      return res;
    } catch (e) {
      this.logger.error(`[Cookie create/update user error] ${e}`);
    } finally {
      this.deleteCache();
    }
  }

  async deleteUnusedSessions(username, browser) {
    try {
      const res = await this.nhstAuthClient.deleteUnusedSession(username, browser || '');
      this.logger.debug('[Delete unused session Success]');
      return res;
    } catch (e) {
      this.logger.error(`[Delete unused session error]`);
      return {};
    }
  }

  async updateLastSeen() {
    try {
      return await this.nhstAuthClient.updateLastSeen();
    } catch (e) {
      this.logger.error(`[Update Last Accessed at Error]`);
      this.logger.error(e);
    }
    return {};
  }

  async hasConcurrencyLimitExceeded() {
    this.logger.info('Checking concurrency exceed');
    try {
      const res = await this.nhstAuthClient.hasConcurrencyLimitExceeded();
      this.logger.info(`[Concurrency limit exceed success]`);
      return res;
    } catch (e) {
      this.logger.error(`[Concurrency limit exceed error] ${e}`);
    }
    return {};
  }

  async triggerAndSendLoginFailEvent(data) {
    const status = data.status || 500;
    const cause = this.nhstAuthClient.getLoginFailureCause(status);
    const username = data.username;
    await this.sendEvent(
      this.nhstAuthClient.EVENT_TYPES.LOGIN_FAIL,
      cause,
      username
    );
    this.triggerLoginFailEvent(status, cause);
    return cause;
  }

  async migrate(username, password) {
    const app = this;
    const doMigrate = async function () {
      try {
        const responses = await Promise.all([
          app.nhstAuthClient.migrate(username, password),
          app.deleteUnusedSessions(username)
        ]);
        // only care about response of the migrate endpoint
        return responses[0];
      } catch (e) {
        app.logger.error(e);
        throw e;
      }
    };
    return app.traceIdDecorator(doMigrate);
  }

  async login(username, password, publicationName, shouldCallRefreshUser = false) {
    const app = this;
    const doLogin = async function () {
      try {
        await Promise.all([
          app.nhstAuthClient.migrate(username, password),
          app.deleteUnusedSessions(username)
        ]);
        const loginResponse = await app.zephrPublicClient.login(username, password);
        const ssoData = {
          email: username
        };

        await Utils.delayForZephrLogin();

        if (shouldCallRefreshUser) {
          try {
            await app.refreshUser();
          } catch (e) {
            app.logger.error(e);
          }
        }

        await Promise.all([app.createOrUpdateCookies(), app.updateSsoMeta(publicationName, ssoData)]);
        await app.sendEvent(app.nhstAuthClient.EVENT_TYPES.LAST_LOGIN, '');
        return loginResponse;
      } catch (e) {
        app.logger.error(e);
        e.cause = await app.triggerAndSendLoginFailEvent(Object.assign({ username }, e.response || {}));
        throw e;
      }
    };
    return app.traceIdDecorator(doLogin);
  }

  async ssoLogin(publicationName, ssoData) {
    const app = this;
    const doSsoLogin = async function () {
      try {
        const ssoResponse = await app.zephrPublicClient.ssoLogin();
        window.parent.postMessage({
          type: 'zephr-sso-login',
          value: true
        });
        await Utils.delayForZephrLogin();
        await Promise.all([app.createOrUpdateCookies(), app.updateSsoMeta(publicationName, ssoData)]);
        await app.sendEvent(app.nhstAuthClient.EVENT_TYPES.LAST_LOGIN, '');
        app.removeTraceIdHeader();
        return ssoResponse;
      } catch (e) {
        app.removeTraceIdHeader();
        app.logger.error(e);
        e.cause = await app.triggerAndSendLoginFailEvent(Object.assign({ username: ssoData.email }, e.response || {}));
        throw e;
      }
    };
    return app.traceIdDecorator(doSsoLogin);
  }

  async logout(type = '') {
    const app = this;
    type = type || app.zephrPublicClient.LOGOUT_TYPES.JUST_HERE;
    const doLogout = async function () {
      try {
        return await Promise.all([
          app.sendEvent(app.nhstAuthClient.EVENT_TYPES.LOGOUT, ''),
          app.clearCookies(),
          app.zephrPublicClient.logout(type),
        ]);
      } catch (e) {
        app.logger.error(e);
      } finally {
        app.deleteCache();
      }
    };
    return app.traceIdDecorator(doLogout);
  }

  async ssoLogout() {
    const app = this;
    const doSsoLogout = async function () {
      try {
        return app.logout(app.zephrPublicClient.LOGOUT_TYPES.THIS_DEVICE);
      } catch (e) {
        app.logger.error(e);
      } finally {
        app.deleteCache();
      }
    };
    return app.traceIdDecorator(doSsoLogout);
  }

  async blaizeAccount() {
    try {
      return this.zephrPublicClient.account();
    } catch (e) {
      return {};
    }
  }

  async getProductShareSummary() {
    const app = this;
    const doGetProductShareSummary = async function () {
      try {
        const response = await app.zephrPublicClient.getProductShareSummary();
        app.removeTraceIdHeader();
        return response;
      } catch (e) {
        app.removeTraceIdHeader();
        app.logger.error(e);
        throw e;
      }
    };
    return app.traceIdDecorator(doGetProductShareSummary);
  }

  async getProductInviteData(sharingId) {
    const app = this;
    const doGetProductInviteData = async function () {
      try {
        const response = await app.zephrPublicClient.getProductInviteData(sharingId);
        app.removeTraceIdHeader();
        return response;
      } catch (e) {
        app.removeTraceIdHeader();
        app.logger.error(e);
        throw e;
      }
    };
    return app.traceIdDecorator(doGetProductInviteData);
  }

  async acceptProductShare(sharingId) {
    const app = this;
    const doAcceptProductShare = async function () {
      try {
        const response = await app.zephrPublicClient.acceptProductShareInvite(sharingId);
        await Utils.delayForZephrLogin();
        await Promise.all([
          app.createOrUpdateCookies(),
          app.sendEvent(
            app.nhstAuthClient.EVENT_TYPES.PRODUCT_SHARE_ACCEPT,
            '',
            '',
            { sharing_id: sharingId }
          )
        ]);
        app.removeTraceIdHeader();
        return response;
      } catch (e) {
        app.removeTraceIdHeader();
        app.logger.error(e);
        throw e;
      }
    };
    return app.traceIdDecorator(doAcceptProductShare);
  }

  async sendProductShareInvite(productId, emailAddress, metaData) {
    const app = this;
    const doSendProductShareInvite = async function () {
      try {
        const baseUrl = `${window.location.origin}/auth/user/product-shares/accept`;
        const response = await app.zephrPublicClient.createProductShareInvite(productId, emailAddress, baseUrl, metaData);
        const sharingId = response?.data?.sharing_id || '';
        await app.sendEvent(
          app.nhstAuthClient.EVENT_TYPES.PRODUCT_SHARE_INVITE,
          '',
          '',
          { member_email: emailAddress, sharing_id: sharingId }
        );
        app.removeTraceIdHeader();
        return response;
      } catch (e) {
        app.removeTraceIdHeader();
        app.logger.error(e);
        throw e;
      }
    };
    return app.traceIdDecorator(doSendProductShareInvite);
  }

  async deleteProductShare(sharingId) {
    const app = this;
    const doDeleteProductShare = async function () {
      try {
        const response = await app.zephrPublicClient.deleteProductShare(sharingId);
        await Utils.delayForZephrLogin();
        await Promise.all([
          app.createOrUpdateCookies(),
          app.sendEvent(
            app.nhstAuthClient.EVENT_TYPES.PRODUCT_SHARE_REVOKE,
            '',
            '',
            { sharing_id: sharingId }
          ),
        ]);
        app.removeTraceIdHeader();
        return response;
      } catch (e) {
        app.removeTraceIdHeader();
        app.logger.error(e);
        throw e;
      }
    };
    return app.traceIdDecorator(doDeleteProductShare);
  }

  async corporateSsoLogin(data = {}, publicationName) {
    const app = this;
    if (data && data.cookie) {
      document.cookie = data.cookie;
    }
    const doCorporateSsoLogin = async function () {
      let username = '';
      try {
        await Utils.delayForZephrLogin();

        const accResponse = await app.zephrPublicClient.account();
        username = accResponse.data.identifiers.email_address;
        await app.nhstAuthClient.corporateSsoOidcFinalize(data);
        await app.nhstAuthClient.corporateSsoOidcFinalizeContinue();
        const ssoData = {
          email: username
        };
        await Promise.all([app.createOrUpdateCookies(), app.updateSsoMeta(publicationName, ssoData)]);
        await app.sendEvent(app.nhstAuthClient.EVENT_TYPES.LAST_LOGIN, '');
        return { status: 'ok' };
      } catch (e) {
        app.logger.error(e);
        e.cause = await app.triggerAndSendLoginFailEvent(Object.assign({ username }, e.response || {}));
        throw e;
      }
    };
    return app.traceIdDecorator(doCorporateSsoLogin);
  }

  // logout from this browser when logged in other sites but not in current site
  async logoutFromThisBrowserWhenSsoStatusIsKnown() {
    const app = this;
    const doSsoLogout = async function () {
      try {
        const ssoResponse = await app.nhstAuthClient.logout(app.nhstAuthClient.LOGOUT_TYPES.SSO_TOKEN);
        window.parent.postMessage({
          type: 'zephr-sso-logout',
          value: true
        });
        return ssoResponse;
      } catch (e) {
        app.logger.error(e);
      } finally {
        app.deleteCache();
      }
    };
    return app.traceIdDecorator(doSsoLogout);
  }

  async changePassword(username, oldPassword, newPassword) {
    const app = this;
    const doChangePassword = async function () {
      try {
        const response = await app.zephrPublicClient.changePassword(oldPassword, newPassword);
        try {
          await app.nhstAuthClient.changeNcpPassword(username, newPassword);
        } catch (err) {
          app.logger.error(err);
        }
        return response;
      } catch (e) {
        app.logger.error(e);
        throw e;
      }
    };
    return app.traceIdDecorator(doChangePassword);
  }

  async resetPasswordComplete(username, newPassword, code) {
    const app = this;
    const doResetPasswordComplete = async function () {
      try {
        const response = await app.zephrPublicClient.resetPasswordComplete(username, newPassword, code);
        try {
          await app.nhstAuthClient.changeNcpPassword(username, newPassword);

          if (Utils.isIOSApp()) {
            try {
              window.webkit.messageHandlers.resetPassword.postMessage({
                type: 'password_reset',
                password_resetted: true,
                username,
              });
            } catch (e) {
            }
          }
          if (Utils.isAndroidApp()) {
            try {
              await app.subAuthClient.mobileResetPasswordPing(username, true);
            } catch (e) {
            }
          }
        } catch (err) {
          app.logger.error(err);
        }
        return response;
      } catch (e) {
        app.logger.error(e);
        throw e;
      }
    };
    return app.traceIdDecorator(doResetPasswordComplete);
  }

  async getSsoStatus() {
    try {
      return await this.zephrPublicClient.getSsoStatus();
    } catch (e) {
      this.logger.error(e);
    }
    return {};
  }

  async clearCookies() {
    let res = {};
    try {
      res = await this.nhstAuthClient.clearCookies();
    } catch (e) {
      this.logger.error(e);
    } finally {
      if (!Utils.isServerSide()) {
        const localStorageRemoveItemKeys = [
          'my-company-access',
          'my-alert-subscriptions',
          'nhst-auth-last-updated-at',
          'sso-status',
          'concurrency-exceeded',
          'sub-error',
          'nhst-auth-purchased-product',
          'nhst-auth-verified-username',
          'sub-user-data'
        ];
        localStorageRemoveItemKeys.forEach(key => localStorage.removeItem(key));
        // remove cookies from client side if clearing cookies failed on server side
        const fallbackManualRemoveCookies = [
          'oneid_info',
          'VPW_ID'
        ];
        fallbackManualRemoveCookies.forEach(cookie => Utils.removeCookie(cookie));
      }
    }
    if (Utils.isIOSApp()) {
      try {
        window.webkit.messageHandlers.cookiesSynced.postMessage({
          type: 'cookie_syncronization',
          session_valid: false,
          cookies_updated: true,
        });
      } catch (e) {
      }
    }
    if (Utils.isAndroidApp()) {
      try {
        await this.subAuthClient.cookiesSynchronizationPing(false, true);
      } catch (e) {
      }
    }
    return res;
  }

  async logInServer(level, message, client = 'SUB-AUTH-WEBAPP') {
    try {
      return this.nhstAuthClient.logInServer(level, message, client);
    } catch (e) {
      console.error('Error sending log in server', e);
      return {};
    }
  }

  async getCorporateSsoConfig() {
    try {
      return this.nhstAuthClient.getCorporateSsoConfig();
    } catch (e) {
      console.error('Error getting corporate sso config', e);
    }
    return {};
  }

  async isCorporateSso(username) {
    try {
      return await this.nhstAuthClient.isCorporateSso(username);
    } catch (e) {
      this.logger.error('Error checking corporate sso', e);
      throw e;
    }
  }

  async createGiftToken(path) {
    const res = await this.zephrPublicClient.createGiftToken(path);
    const getArticleId = (path) => {
      try {
        return (path || '').split('/').pop();
      } catch (e) {
        console.error(`[Sub Gift Article Token] Error getting article id for path: ${path}`);
      }
      return path;
    };
    await this.sendEvent(
      this.nhstAuthClient.EVENT_TYPES.GIFT_ARTICLE_TOKEN_CREATED,
      '',
      '',
      { gift_article_path: path, gift_article_id: getArticleId(path) }
    );
    return res;
  }

  async updateCustomSegmentsInZephr(data) {
    try {
      return this.zephrPublicClient.updateCustomSegments(data);
    } catch (e) {
      console.error('Error updating custom segments in Zephr', e);
    }
    return {};
  }

  async resendServerLogs() {
    while (true) {
      try {
        const storedConfig = getServerLog();
        if (!storedConfig) {
          return;
        }
        storedConfig.data = storedConfig.data || {};
        storedConfig.data.message = storedConfig.data.message || '';
        if (!storedConfig.data.message.startsWith('[Resending Log]')) {
          storedConfig.data.message = `[Resending Log] ${storedConfig.data.message}`;
        }
        storedConfig.message = storedConfig.data.message;
        this.nhstAuthClient.serverLog(storedConfig);
        // wait few miliseconds to send another server log
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (e) {
        this.logger.error(e);
      }
    }
  }
}

export { AuthServices };
